import type { FC } from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import {
    LuType, LuHeading1, LuHeading2, LuBold, LuItalic, LuLink,
    LuList, LuListOrdered, LuSquareCheck, LuQuote, LuCode, LuMinus, LuImage,
} from 'react-icons/lu';
import { escolherImagemDaAnotacao } from './NoteEditor';

interface NoteToolbarProps {
    editor: Editor;
    /** Nulo enquanto a anotação não tem id — a imagem sobe solta e é vinculada depois. */
    anotacaoId: number | null;
}

/**
 * Barra de edição da anotação — Direção A da proposta de 2026-09-12.
 *
 * Existe porque o editor não oferecia porta nenhuma: ao abrir uma anotação com
 * conteúdo não havia nada visível, e a única dica do "/" era o placeholder, que
 * só aparece em parágrafo vazio. A bolha da seleção e o "/" continuam — são o
 * caminho de quem já sabe; a barra é o de quem está descobrindo.
 *
 * Ela NÃO muda de conteúdo conforme o bloco (barra que se reorganiza obriga a
 * reaprender onde as coisas estão); só acende o que está ativo. Desfazer/refazer
 * ficaram de fora: Ctrl+Z é universal e dois ícones custariam mais do que entregam.
 */
const NoteToolbar: FC<NoteToolbarProps> = ({ editor, anotacaoId }) => {
    /* `useEditorState` em vez de ler `editor.isActive` direto no render: sem ele
       a barra não repinta quando o cursor anda, porque mover o cursor não muda
       nenhuma prop do React. */
    const ativo = useEditorState({
        editor,
        selector: ({ editor: e }) => ({
            paragrafo: e.isActive('paragraph'),
            h1: e.isActive('heading', { level: 1 }),
            h2: e.isActive('heading', { level: 2 }),
            negrito: e.isActive('bold'),
            italico: e.isActive('italic'),
            link: e.isActive('link'),
            marcador: e.isActive('bulletList'),
            numerada: e.isActive('orderedList'),
            tarefas: e.isActive('taskList'),
            citacao: e.isActive('blockquote'),
            codigo: e.isActive('codeBlock'),
        }),
    });

    const definirLink = () => {
        const atual = editor.getAttributes('link').href as string | undefined;
        const url = window.prompt('Endereço do link', atual ?? 'https://');
        if (url === null) return;
        if (url.trim() === '') {
            editor.chain().focus().unsetLink().run();
            return;
        }
        editor.chain().focus().setLink({ href: url.trim() }).run();
    };

    const botao = (
        chave: string,
        rotulo: string,
        icone: React.ReactNode,
        aoClicar: () => void,
        ligado = false,
    ) => (
        <button
            key={chave}
            type="button"
            className={`ne-bar-b ${ligado ? 'is-on' : ''}`}
            title={rotulo}
            aria-label={rotulo}
            aria-pressed={ligado}
            /* O clique não pode roubar o cursor do texto, senão o comando cai
               num documento sem seleção e não faz nada. */
            onMouseDown={e => e.preventDefault()}
            onClick={aoClicar}
        >
            {icone}
        </button>
    );

    return (
        <div className="ne-bar" role="toolbar" aria-label="Formatação da anotação">
            <div className="ne-bar-col">
                {botao('p', 'Texto', <LuType size={15} />,
                    () => editor.chain().focus().setParagraph().run(), ativo.paragrafo)}
                {botao('h1', 'Título 1', <LuHeading1 size={15} />,
                    () => editor.chain().focus().toggleHeading({ level: 1 }).run(), ativo.h1)}
                {botao('h2', 'Título 2', <LuHeading2 size={15} />,
                    () => editor.chain().focus().toggleHeading({ level: 2 }).run(), ativo.h2)}

                <span className="ne-bar-sep" />

                {botao('b', 'Negrito', <LuBold size={15} />,
                    () => editor.chain().focus().toggleBold().run(), ativo.negrito)}
                {botao('i', 'Itálico', <LuItalic size={15} />,
                    () => editor.chain().focus().toggleItalic().run(), ativo.italico)}
                {botao('link', 'Link', <LuLink size={15} />, definirLink, ativo.link)}

                <span className="ne-bar-sep" />

                {botao('ul', 'Lista com marcador', <LuList size={15} />,
                    () => editor.chain().focus().toggleBulletList().run(), ativo.marcador)}
                {botao('ol', 'Lista numerada', <LuListOrdered size={15} />,
                    () => editor.chain().focus().toggleOrderedList().run(), ativo.numerada)}
                {botao('task', 'Lista de tarefas', <LuSquareCheck size={15} />,
                    () => editor.chain().focus().toggleTaskList().run(), ativo.tarefas)}

                <span className="ne-bar-sep" />

                {botao('quote', 'Citação', <LuQuote size={15} />,
                    () => editor.chain().focus().toggleBlockquote().run(), ativo.citacao)}
                {botao('code', 'Bloco de código', <LuCode size={15} />,
                    () => editor.chain().focus().toggleCodeBlock().run(), ativo.codigo)}
                {botao('hr', 'Divisória', <LuMinus size={15} />,
                    () => editor.chain().focus().setHorizontalRule().run())}
                {botao('img', 'Imagem', <LuImage size={15} />,
                    () => escolherImagemDaAnotacao(editor, anotacaoId))}
            </div>
        </div>
    );
};

export default NoteToolbar;
